import { useState, useEffect, useCallback } from "react";
import {
    Loader2, Save, UploadCloud, Eye, Plus, Trash2, Building2, Clock,
    MapPin, Wrench, Users, FileText, ShieldAlert, CheckCircle2, AlertTriangle, Sparkles, Layers,
} from "lucide-react";
import {
    fetchAiProfile, patchAiProfile, patchAiProfileTrades, publishAiProfile, fetchAiProfilePreview, formatApiError,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

const DAYS = [
    ["mon", "Mandag"], ["tue", "Tirsdag"], ["wed", "Onsdag"], ["thu", "Torsdag"],
    ["fri", "Fredag"], ["sat", "Lørdag"], ["sun", "Søndag"],
];
const CUSTOMER_TYPES = [
    ["private", "Privat"], ["business", "Erhverv"],
    ["housing_association", "Boligforening"], ["public", "Offentlig"],
];
const FIELD_LABELS = {
    display_name: "Firmanavn", phone: "Telefon", enabled_trades: "Fag/branche",
    opening_hours: "Åbningstider", service_area: "Serviceområde", services: "Mindst én service",
};

function Section({ value, icon: Icon, title, subtitle, children }) {
    return (
        <AccordionItem value={value} className="border border-slate-200/70 rounded-2xl bg-white overflow-hidden data-[state=open]:shadow-sm">
            <AccordionTrigger className="px-5 py-4 hover:no-underline" data-testid={`ai-section-${value}`}>
                <div className="flex items-center gap-3 text-left">
                    <span className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                        <Icon className="w-4.5 h-4.5 text-slate-600" size={18} />
                    </span>
                    <div>
                        <div className="text-sm font-semibold text-slate-900">{title}</div>
                        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-1">{children}</AccordionContent>
        </AccordionItem>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">{label}</Label>
            {children}
        </div>
    );
}

export default function AICompanyDataTab({ canEdit }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [meta, setMeta] = useState(null);
    const [preset, setPreset] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetchAiProfile();
            setDraft(d.draft);
            setPreset(d.preset || {});
            setMeta({
                profile_status: d.profile_status, profile_version: d.profile_version,
                completeness_percent: d.completeness_percent, missing_fields: d.missing_fields || [],
                published_at: d.published_at, has_unpublished_changes: d.has_unpublished_changes,
                enabled_trades: d.enabled_trades || [], primary_trade: d.primary_trade || null,
                trades_catalog: d.trades_catalog || [],
            });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setBasic = (k, v) => setDraft((p) => ({ ...p, basic: { ...p.basic, [k]: v } }));
    const setHours = (day, k, v) => setDraft((p) => ({
        ...p, opening_hours: { ...p.opening_hours, [day]: { ...(p.opening_hours[day] || {}), [k]: v } },
    }));
    const setHoursFlag = (k, v) => setDraft((p) => ({ ...p, opening_hours: { ...p.opening_hours, [k]: v } }));
    const setArea = (k, v) => setDraft((p) => ({ ...p, service_area: { ...p.service_area, [k]: v } }));
    const setCustType = (k, v) => setDraft((p) => ({ ...p, customer_types: { ...p.customer_types, [k]: v } }));
    const setPolicy = (k, v) => setDraft((p) => ({ ...p, policies: { ...p.policies, [k]: v } }));
    const toggleService = (slug, v) => setDraft((p) => ({
        ...p, services: p.services.map((s) => (s.slug === slug ? { ...s, enabled: v } : s)),
    }));

    const addEmployee = () => setDraft((p) => ({
        ...p, employees: [...(p.employees || []), { name: "", role: "", specialties: [], ai_visible: true }],
    }));
    const setEmployee = (i, k, v) => setDraft((p) => ({
        ...p, employees: p.employees.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)),
    }));
    const removeEmployee = (i) => setDraft((p) => ({ ...p, employees: p.employees.filter((_, idx) => idx !== i) }));

    const addFaq = () => setDraft((p) => ({
        ...p, faq: [...(p.faq || []), { question: "", answer: "", active: true }],
    }));
    const setFaq = (i, k, v) => setDraft((p) => ({
        ...p, faq: p.faq.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)),
    }));
    const removeFaq = (i) => setDraft((p) => ({ ...p, faq: p.faq.filter((_, idx) => idx !== i) }));

    const [savingTrades, setSavingTrades] = useState(false);
    const saveTrades = async (primary_trade, enabled_trades) => {
        if (!canEdit) return;
        setSavingTrades(true);
        try {
            const d = await patchAiProfileTrades({ primary_trade, enabled_trades });
            setDraft(d.draft);
            setPreset(d.preset || {});
            setMeta((m) => ({
                ...m, ...d, missing_fields: d.missing_fields || [],
                primary_trade: d.primary_trade, enabled_trades: d.enabled_trades || [],
                trades_catalog: d.trades_catalog || m.trades_catalog,
            }));
            toast.success("Brancher opdateret");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingTrades(false); }
    };
    const toggleTrade = (id) => {
        const cur = meta?.enabled_trades || [];
        let next = cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id];
        if (next.length === 0) { toast.error("Vælg mindst én branche"); return; }
        let primary = meta?.primary_trade;
        if (!next.includes(primary)) primary = next[0];
        saveTrades(primary, next);
    };
    const setPrimary = (id) => {
        const cur = meta?.enabled_trades || [];
        const next = cur.includes(id) ? cur : [...cur, id];
        saveTrades(id, next);
    };

    const save = async () => {
        if (!canEdit) return;
        setSaving(true);
        try {
            const payload = {
                basic: draft.basic, opening_hours: draft.opening_hours, service_area: draft.service_area,
                customer_types: draft.customer_types, policies: draft.policies,
                services: draft.services.map((s) => ({ slug: s.slug, enabled: s.enabled })),
                employees: draft.employees, faq: draft.faq,
            };
            const d = await patchAiProfile(payload);
            setDraft(d.draft);
            setMeta((m) => ({ ...m, ...d, missing_fields: d.missing_fields || [] }));
            toast.success("Gemt");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const publish = async () => {
        if (!canEdit) return;
        setPublishing(true);
        try {
            await save();
            const res = await publishAiProfile();
            toast.success(`Publiceret — version ${res.profile_version}`);
            await load();
            await loadPreview();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "incomplete") {
                const names = (detail.missing_fields || []).map((f) => FIELD_LABELS[f] || f).join(", ");
                toast.error(`Kan ikke publiceres — mangler: ${names}`);
                await load();
            } else { toast.error(formatApiError(e)); }
        } finally { setPublishing(false); }
    };

    const loadPreview = async () => {
        setPreviewLoading(true);
        try { setPreview(await fetchAiProfilePreview()); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setPreviewLoading(false); }
    };

    if (loading || !draft) {
        return <div className="flex items-center gap-2 text-slate-500 py-16 justify-center" data-testid="ai-data-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Indlæser AI-profil…
        </div>;
    }

    const statusMap = {
        published: { l: "Publiceret", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        incomplete: { l: "Ufuldstændig", c: "bg-amber-50 text-amber-700 border-amber-200" },
        draft: { l: "Kladde", c: "bg-slate-100 text-slate-600 border-slate-200" },
    };
    const st = statusMap[meta?.profile_status] || statusMap.draft;

    return (
        <div className="space-y-5" data-testid="ai-data-tab">
            {/* Status header */}
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-5 flex flex-wrap items-center gap-4 justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-slate-900">AI & Virksomhedsdata</h3>
                            <Badge className={`border ${st.c}`} data-testid="ai-profile-status">{st.l}</Badge>
                            <span className="text-xs text-slate-400" data-testid="ai-profile-version">v{meta?.profile_version || 0}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Ét sted til de data AI-telefonen bruger. Kun publicerede data bruges af AI.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-xs text-slate-400">Udfyldt</div>
                        <div className="text-lg font-bold text-slate-900" data-testid="ai-completeness">{meta?.completeness_percent || 0}%</div>
                    </div>
                    {canEdit && (
                        <>
                            <Button variant="outline" onClick={save} disabled={saving} data-testid="ai-save-btn">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Gem
                            </Button>
                            <Button onClick={publish} disabled={publishing} data-testid="ai-publish-btn"
                                className="bg-emerald-600 hover:bg-emerald-700">
                                {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}Publicér
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {(meta?.missing_fields?.length > 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="ai-missing-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                        <span className="font-semibold">Mangler før AI kan bruge profilen:</span>{" "}
                        {meta.missing_fields.map((f) => FIELD_LABELS[f] || f).join(", ")}
                    </div>
                </div>
            )}

            <Accordion type="multiple" defaultValue={["trades", "basic"]} className="space-y-3">
                {/* Brancher (canonical primary + additional) */}
                <Section value="trades" icon={Layers} title="Brancher" subtitle="Primær og øvrige fag — deles med admin">
                    <div className="space-y-5">
                        <div>
                            <Label className="text-xs text-slate-600">Primær branche *</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {(meta?.trades_catalog || []).map((t) => {
                                    const active = meta?.primary_trade === t.id;
                                    return (
                                        <button key={t.id} type="button" disabled={!canEdit || savingTrades}
                                            onClick={() => setPrimary(t.id)} data-testid={`ai-primary-${t.id}`}
                                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                                            {t.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-slate-600">Andre brancher</Label>
                            <div className="grid sm:grid-cols-2 gap-2 mt-2">
                                {(meta?.trades_catalog || []).map((t) => {
                                    const enabled = (meta?.enabled_trades || []).includes(t.id);
                                    const isPrimary = meta?.primary_trade === t.id;
                                    return (
                                        <label key={t.id} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-xl border ${enabled ? "border-blue-200 bg-blue-50/40" : "border-slate-100 bg-slate-50/40"}`} data-testid={`ai-trade-toggle-${t.id}`}>
                                            <span className="text-sm text-slate-700">{t.name}{isPrimary && <span className="ml-2 text-[10px] text-blue-600 font-medium">PRIMÆR</span>}{!t.has_preset && <span className="ml-2 text-[10px] text-slate-400">(ingen preset endnu)</span>}</span>
                                            <Switch checked={enabled} disabled={!canEdit || savingTrades || isPrimary} onCheckedChange={() => toggleTrade(t.id)} data-testid={`ai-trade-switch-${t.id}`} />
                                        </label>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-2">Nye brancher tilføjer deres services slået fra — vælg selv de relevante under "Services". Fjernes en branche, medtages den ikke i publiceret AI-data.</p>
                        </div>
                    </div>
                </Section>

                {/* Grundinfo */}
                <Section value="basic" icon={Building2} title="Grundinfo" subtitle="Firmanavn, kontakt og adresse">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Vis-navn *"><Input data-testid="ai-display-name" value={draft.basic.display_name || ""} onChange={(e) => setBasic("display_name", e.target.value)} disabled={!canEdit} /></Field>
                        <Field label="Juridisk navn"><Input data-testid="ai-legal-name" value={draft.basic.legal_name || ""} onChange={(e) => setBasic("legal_name", e.target.value)} disabled={!canEdit} /></Field>
                        <Field label="Telefon *"><Input data-testid="ai-phone" value={draft.basic.phone || ""} onChange={(e) => setBasic("phone", e.target.value)} disabled={!canEdit} /></Field>
                        <Field label="Email"><Input data-testid="ai-email" value={draft.basic.email || ""} onChange={(e) => setBasic("email", e.target.value)} disabled={!canEdit} /></Field>
                        <Field label="Adresse"><Input data-testid="ai-address" value={draft.basic.address || ""} onChange={(e) => setBasic("address", e.target.value)} disabled={!canEdit} /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Postnr."><Input data-testid="ai-postal" value={draft.basic.postal_code || ""} onChange={(e) => setBasic("postal_code", e.target.value)} disabled={!canEdit} /></Field>
                            <Field label="By"><Input data-testid="ai-city" value={draft.basic.city || ""} onChange={(e) => setBasic("city", e.target.value)} disabled={!canEdit} /></Field>
                        </div>
                        <Field label="Website"><Input data-testid="ai-website" value={draft.basic.website || ""} onChange={(e) => setBasic("website", e.target.value)} disabled={!canEdit} /></Field>
                        <div className="sm:col-span-2">
                            <Field label="Kort beskrivelse"><Textarea data-testid="ai-short-desc" rows={2} value={draft.basic.short_description || ""} onChange={(e) => setBasic("short_description", e.target.value)} disabled={!canEdit} /></Field>
                        </div>
                    </div>
                </Section>

                {/* Åbningstider */}
                <Section value="hours" icon={Clock} title="Åbningstider" subtitle="Ugentlige tider og akut-service">
                    <div className="space-y-2">
                        {DAYS.map(([k, label]) => {
                            const day = draft.opening_hours[k] || {};
                            return (
                                <div key={k} className="flex items-center gap-3 flex-wrap" data-testid={`ai-hours-${k}`}>
                                    <div className="w-24 text-sm text-slate-700">{label}</div>
                                    <label className="flex items-center gap-2 text-xs text-slate-500">
                                        <Switch checked={!!day.closed} onCheckedChange={(v) => setHours(k, "closed", v)} disabled={!canEdit} data-testid={`ai-hours-${k}-closed`} />
                                        Lukket
                                    </label>
                                    {!day.closed && (
                                        <>
                                            <Input type="time" className="w-32" value={day.open || ""} onChange={(e) => setHours(k, "open", e.target.value)} disabled={!canEdit} data-testid={`ai-hours-${k}-open`} />
                                            <span className="text-slate-400">–</span>
                                            <Input type="time" className="w-32" value={day.close || ""} onChange={(e) => setHours(k, "close", e.target.value)} disabled={!canEdit} data-testid={`ai-hours-${k}-close`} />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                        <div className="flex flex-wrap gap-6 pt-3 border-t border-slate-100 mt-3">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <Switch checked={!!draft.opening_hours.emergency_outside_hours} onCheckedChange={(v) => setHoursFlag("emergency_outside_hours", v)} disabled={!canEdit} data-testid="ai-emergency" />
                                Akut uden for åbningstid
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <Switch checked={!!draft.opening_hours.weekend_service} onCheckedChange={(v) => setHoursFlag("weekend_service", v)} disabled={!canEdit} data-testid="ai-weekend" />
                                Weekend-service
                            </label>
                        </div>
                    </div>
                </Section>

                {/* Serviceområde */}
                <Section value="area" icon={MapPin} title="Serviceområde" subtitle="Hvor I arbejder">
                    <div className="space-y-4">
                        <Field label="Beskrivelse *"><Textarea data-testid="ai-area-desc" rows={2} value={draft.service_area.description || ""} onChange={(e) => setArea("description", e.target.value)} disabled={!canEdit} placeholder="fx Hele Fyn og omegn" /></Field>
                        <Field label="Undtagelser (én pr. linje)">
                            <Textarea data-testid="ai-area-exclusions" rows={2}
                                value={(draft.service_area.exclusions || []).join("\n")}
                                onChange={(e) => setArea("exclusions", e.target.value.split("\n"))}
                                disabled={!canEdit} placeholder="fx Øer uden broforbindelse" />
                        </Field>
                        <div>
                            <Label className="text-xs text-slate-600">Kundetyper</Label>
                            <div className="flex flex-wrap gap-4 mt-2">
                                {CUSTOMER_TYPES.map(([k, label]) => (
                                    <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
                                        <Switch checked={!!draft.customer_types[k]} onCheckedChange={(v) => setCustType(k, v)} disabled={!canEdit} data-testid={`ai-custtype-${k}`} />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* Services */}
                <Section value="services" icon={Wrench} title="Services" subtitle="Slå de ydelser til I tilbyder">
                    {draft.services.length === 0 ? (
                        <p className="text-sm text-slate-500">Ingen services — vælg en branche med preset (fx El) under "Brancher".</p>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-2">
                            {draft.services.map((s) => (
                                <label key={s.slug} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`ai-service-${s.slug}`}>
                                    <span className="text-sm text-slate-700">{s.label}</span>
                                    <Switch checked={!!s.enabled} onCheckedChange={(v) => toggleService(s.slug, v)} disabled={!canEdit} data-testid={`ai-service-${s.slug}-toggle`} />
                                </label>
                            ))}
                        </div>
                    )}
                </Section>

                {/* Medarbejdere */}
                <Section value="employees" icon={Users} title="Medarbejdere" subtitle="Kun AI-synlige felter — aldrig private data">
                    <div className="space-y-3">
                        {(draft.employees || []).map((e, i) => (
                            <div key={i} className="grid sm:grid-cols-[1fr_1fr_1.5fr_auto_auto] gap-2 items-center" data-testid={`ai-employee-${i}`}>
                                <Input placeholder="Navn" value={e.name || ""} onChange={(ev) => setEmployee(i, "name", ev.target.value)} disabled={!canEdit} data-testid={`ai-employee-${i}-name`} />
                                <Input placeholder="Rolle" value={e.role || ""} onChange={(ev) => setEmployee(i, "role", ev.target.value)} disabled={!canEdit} data-testid={`ai-employee-${i}-role`} />
                                <Input placeholder="Specialer (komma)" value={(e.specialties || []).join(", ")} onChange={(ev) => setEmployee(i, "specialties", ev.target.value.split(",").map((x) => x.trim()).filter(Boolean))} disabled={!canEdit} data-testid={`ai-employee-${i}-spec`} />
                                <label className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <Switch checked={!!e.ai_visible} onCheckedChange={(v) => setEmployee(i, "ai_visible", v)} disabled={!canEdit} data-testid={`ai-employee-${i}-visible`} /> AI
                                </label>
                                {canEdit && <Button variant="ghost" size="icon" onClick={() => removeEmployee(i)} data-testid={`ai-employee-${i}-remove`}><Trash2 className="w-4 h-4 text-slate-400" /></Button>}
                            </div>
                        ))}
                        {canEdit && <Button variant="outline" size="sm" onClick={addEmployee} data-testid="ai-employee-add"><Plus className="w-4 h-4 mr-1" />Tilføj medarbejder</Button>}
                    </div>
                </Section>

                {/* Kunderegler */}
                <Section value="policies" icon={FileText} title="Kunderegler" subtitle="Tilbud, materialer og betaling">
                    <div className="space-y-3">
                        {[
                            ["quotes_enabled", "Vi giver tilbud"],
                            ["quotes_free", "Tilbud er gratis"],
                            ["ai_may_quote_exact_prices", "AI må nævne konkrete priser"],
                            ["customer_supplied_materials", "Kunden må selv levere materialer"],
                            ["default_call_before_arrival", "Ring altid før ankomst"],
                        ].map(([k, label]) => (
                            <label key={k} className="flex items-center justify-between gap-3 text-sm text-slate-700" data-testid={`ai-policy-${k}`}>
                                {label}
                                <Switch checked={!!draft.policies[k]} onCheckedChange={(v) => setPolicy(k, v)} disabled={!canEdit} data-testid={`ai-policy-${k}-toggle`} />
                            </label>
                        ))}
                        <Field label="Betalingsbetingelser"><Input data-testid="ai-payment-terms" value={draft.policies.payment_terms || ""} onChange={(e) => setPolicy("payment_terms", e.target.value)} disabled={!canEdit} placeholder="fx Netto 8 dage" /></Field>
                    </div>
                </Section>

                {/* FAQ */}
                <Section value="faq" icon={FileText} title="FAQ" subtitle="Spørgsmål AI kan besvare">
                    <div className="space-y-3">
                        {(draft.faq || []).map((f, i) => (
                            <div key={i} className="border border-slate-100 rounded-xl p-3 space-y-2 bg-slate-50/40" data-testid={`ai-faq-${i}`}>
                                <div className="flex items-center gap-2">
                                    <Input placeholder="Spørgsmål" value={f.question || ""} onChange={(e) => setFaq(i, "question", e.target.value)} disabled={!canEdit} data-testid={`ai-faq-${i}-q`} />
                                    <label className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
                                        <Switch checked={!!f.active} onCheckedChange={(v) => setFaq(i, "active", v)} disabled={!canEdit} data-testid={`ai-faq-${i}-active`} />Aktiv
                                    </label>
                                    {canEdit && <Button variant="ghost" size="icon" onClick={() => removeFaq(i)} data-testid={`ai-faq-${i}-remove`}><Trash2 className="w-4 h-4 text-slate-400" /></Button>}
                                </div>
                                <Textarea placeholder="Svar" rows={2} value={f.answer || ""} onChange={(e) => setFaq(i, "answer", e.target.value)} disabled={!canEdit} data-testid={`ai-faq-${i}-a`} />
                            </div>
                        ))}
                        {canEdit && <Button variant="outline" size="sm" onClick={addFaq} data-testid="ai-faq-add"><Plus className="w-4 h-4 mr-1" />Tilføj spørgsmål</Button>}
                    </div>
                </Section>

                {/* Branchedata → dynamisk pr. aktiv branche med preset */}
                <Section value="trade" icon={ShieldAlert}
                    title={`Branchedata${Object.keys(preset).length ? " (" + Object.values(preset).map((c) => c.label).join(", ") + ")" : ""}`}
                    subtitle="Sikkerhedssignaler AI lytter efter">
                    {Object.keys(preset).length === 0 ? (
                        <p className="text-sm text-slate-500">Intet fag-preset aktivt. Vælg fx El under "Brancher".</p>
                    ) : Object.entries(preset).map(([trade, cfg]) => (
                        <div key={trade} className="space-y-2 mb-4 last:mb-0" data-testid={`ai-trade-${trade}`}>
                            <div className="text-sm font-medium text-slate-800">Branchedata ({cfg.label})</div>
                            <div className="flex flex-wrap gap-2">
                                {(cfg.safety_signals || []).map((sig) => (
                                    <Badge key={sig} variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{sig}</Badge>
                                ))}
                            </div>
                        </div>
                    ))}
                </Section>

                {/* Preview AI-data */}
                <Section value="preview" icon={Eye} title="Preview AI-data" subtitle="Præcis den data ElevenLabs modtager">
                    <div className="space-y-3">
                        <Button variant="outline" size="sm" onClick={loadPreview} disabled={previewLoading} data-testid="ai-preview-load">
                            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}Vis publiceret AI-data
                        </Button>
                        {preview && (
                            <div data-testid="ai-preview-panel">
                                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-2">
                                    <span>Status: <b className="text-slate-700">{preview.profile_status}</b></span>
                                    <span>Version: <b className="text-slate-700">{preview.profile_version}</b></span>
                                    <span>Udfyldt: <b className="text-slate-700">{preview.completeness_percent}%</b></span>
                                    {preview.published ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-amber-600">Ikke publiceret endnu</span>}
                                </div>
                                <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-xl p-4 overflow-auto max-h-96" data-testid="ai-preview-json">
{JSON.stringify(preview.payload, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                </Section>
            </Accordion>
        </div>
    );
}
